import { useResizeObserver } from "@ui/hooks/use-resize-observer";
import { tv } from "@ui/lib/tv";
import { useCallback, useEffect, useRef, useState } from "react";

export type PreviewViewport = "desktop" | "mobile";

export interface PreviewFrameProps {
  /** Same-origin URL to embed. The iframe gets a real, narrow viewport so the
   *  embedded page's responsive breakpoints resolve faithfully. */
  src: string;
  /** Which device frame + logical viewport width to render at, or "fill" to
   *  render the bare sandboxed iframe edge-to-edge in its parent — for when the
   *  surrounding container already provides the intended viewport (e.g. a
   *  full-screen preview modal on an actual phone). */
  viewport: PreviewViewport | "fill";
  /** Accessible name for the iframe. */
  title: string;
  className?: string;
}

// The logical viewport the embedded page renders at. This is the whole point of
// the iframe: a desktop `<div>` constrained to 390px still renders the *desktop*
// layout because Tailwind breakpoints follow the real viewport — an iframe gives
// the page a genuinely narrow viewport instead.
const DIMENSIONS: Record<PreviewViewport, { width: number; height: number }> = {
  desktop: { width: 1280, height: 760 },
  mobile: { width: 390, height: 680 },
};

const styles = tv({
  slots: {
    root: "flex w-full justify-center",
    // Browser-window chrome for desktop; phone bezel for mobile.
    chromeDesktop: "overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-lg",
    chromeMobile: "overflow-hidden rounded-[2rem] border-[6px] border-neutral-900 bg-neutral-900 shadow-xl",
    titleBar: "flex h-8 items-center gap-1.5 border-neutral-100 border-b bg-neutral-50 px-3",
    dot: "size-2.5 rounded-full",
    // Clipping window the scaled iframe sits inside.
    viewport: "relative overflow-hidden bg-white",
    scaler: "origin-top-left",
    iframe: "block border-0 bg-white",
  },
});

export function PreviewFrame({ src, viewport, title, className }: PreviewFrameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const onResize = useCallback(() => {
    if (containerRef.current) setContainerWidth(containerRef.current.clientWidth);
  }, []);
  useResizeObserver({ ref: containerRef, onResize });
  // useResizeObserver's no-ResizeObserver fallback never fires an initial measure, so
  // take one on mount — otherwise the iframe (gated on scale > 0) stays blank until a
  // window resize. Harmless when ResizeObserver is present (it measures again).
  useEffect(() => onResize(), [onResize]);

  const css = styles();

  if (viewport === "fill") {
    return (
      // Trusted same-origin app page; allow-scripts + allow-same-origin are
      // required for the embedded React app to boot. Nothing else is granted —
      // the preview is read-only (inert), so no forms/popups/navigation.
      <iframe
        className={css.iframe({ className: ["size-full", className] })}
        sandbox="allow-scripts allow-same-origin"
        src={src}
        title={title}
      />
    );
  }

  const { width, height } = DIMENSIONS[viewport];
  // Never upscale past 1 — a phone frame shouldn't balloon on a wide screen.
  const scale = containerWidth > 0 ? Math.min(containerWidth / width, 1) : 0;
  const isMobile = viewport === "mobile";

  const viewportPane = (
    <div className={css.viewport()} style={{ width: width * scale, height: height * scale }}>
      <div className={css.scaler()} style={{ transform: `scale(${scale})` }}>
        {scale > 0 ? (
          // Trusted same-origin app page; allow-scripts + allow-same-origin are
          // required for the embedded React app to boot. Nothing else is granted —
          // the preview is read-only (inert), so no forms/popups/navigation.
          <iframe
            className={css.iframe()}
            height={height}
            sandbox="allow-scripts allow-same-origin"
            src={src}
            title={title}
            width={width}
          />
        ) : null}
      </div>
    </div>
  );

  return (
    <div className={css.root({ className })} ref={containerRef}>
      {isMobile ? (
        <div className={css.chromeMobile()}>{viewportPane}</div>
      ) : (
        <div className={css.chromeDesktop()} style={{ width: width * scale }}>
          <div className={css.titleBar()}>
            <span className={css.dot({ className: "bg-red-400" })} />
            <span className={css.dot({ className: "bg-amber-400" })} />
            <span className={css.dot({ className: "bg-green-400" })} />
          </div>
          {viewportPane}
        </div>
      )}
    </div>
  );
}
